// Dependencies
import React from 'react';
import { Impression } from '@nerdwallet/analytics/react/impression';

// Utils
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import { filter, find } from 'lodash';

// Local components
import { Box } from '@nerdwallet/currency';
import {
  ENTITY_NAME,
  FORM_NAME,
  SECTION_NAME,
} from '../../../../lib/analytics-constants';
import calculateCompareData from '../../../../lib/col/col-calculator';

import CitySuggestions from '../../city-suggestions/city-suggestions';
import ColResultsBody from '../col-results-body/col-results-body';
import CityResultsError from '../../shared/city-results-error/city-results-error';
import CityLoading from '../../shared/city-loading/city-loading';
import ColResultsHeader from '../results-header/results-header';

// Config & Data
import { CompareDataProps, ViewConfigProps } from '../../shared/types';

// CSS
require('../../shared/react-select-overrides.less');

type COLResultsPanelProps = {
  data: {
    cities: Record<string, string>[];
    compareData: CompareDataProps;
    inputs: {
      destinationCity: string;
      income: number;
      sourceCity: string;
    };
    suggestedCityData: Record<string, any>[];
    validations: {
      valid: boolean;
    };
  };
  history: Record<string, any>;
  loading: boolean;
  location: Record<string, any>;
  onChange: (config: any, value: any) => void;
  handleInputChange: (data: Record<string, any>) => void;
  resultsTableConfig: ViewConfigProps[];
};

const COLResultsPanel = ({
  data,
  onChange,
  handleInputChange,
  loading,
  resultsTableConfig,
}: COLResultsPanelProps): JSX.Element => {
  const income = data?.inputs?.income;
  const { cities, suggestedCityData } = data;
  const sourceCity = data?.inputs?.sourceCity;
  const sourceCityData = find(cities, { id: sourceCity });
  const destinationCity = data?.inputs?.destinationCity;
  const destinationCityData = find(cities, { id: destinationCity });
  const compareData = data?.compareData;

  const filteredSuggestedCityData = filter(
    suggestedCityData,
    (item) => item.id !== sourceCity
  ).slice(0, 6);

  const suggestedCityCompareData = filteredSuggestedCityData.map((city) =>
    calculateCompareData(sourceCityData, city, income)
  );

  const citySuggestionsProps = {
    handleInputChange,
    sourceCityData,
    suggestedCityData: filteredSuggestedCityData,
    suggestedCityCompareData,
  };

  const colResultsBodyProps = {
    citiesData: {
      sourceCity: sourceCityData,
      destinationCity: destinationCityData,
    },
    compareData,
    handleInputChange,
    resultsTableConfig,
  };

  if (!sourceCityData) {
    // server down and couldn't fetch data, so try to reload page
    const onRefreshClick = () => {
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    };

    return <CityResultsError onRefreshClick={onRefreshClick} />;
  }

  if (loading) {
    return <CityLoading />;
  }

  return (
    <Box>
      <Impression
        eventType="form_impression"
        entityName={ENTITY_NAME.COST_OF_LIVING}
        payload={{
          form_name: FORM_NAME.COST_OF_LIVING,
          section_name: SECTION_NAME.COST_OF_LIVING,
          section_position: 0,
          page_number: 1,
          total_pages: 1,
        }}
      >
        <ColResultsHeader
          cities={cities}
          sourceCity={sourceCity}
          destinationCity={destinationCity}
          onChange={onChange}
        />
      </Impression>
      {destinationCity ? (
        <ColResultsBody {...colResultsBodyProps} />
      ) : (
        <CitySuggestions {...citySuggestionsProps} />
      )}
    </Box>
  );
};

COLResultsPanel.displayName = 'COLResultsPanel';

export default COLResultsPanel;
